<template>
  <div class="container" ref="container">
    <b-loading
      :is-full-page="true"
      v-model="isLoading"
      :can-cancel="true"
    ></b-loading>
    <div class="columns is-multiline mb-5">
      <div class="column is-4">
        <figure class="image is-inline-block">
          <img
            class="is-rounded"
            style="width: 280px"
            src="@/assets/img/customerservice.webp"
          />
        </figure>
      </div>

      <div class="column is-vcentered is-size-4 is-size-6-mobile">
        <div class="">
          <h1 class="title">{{ $t("helpCenter.title") }}</h1>
          {{ $t("helpCenter.description") }}

          <div class="buttons mt-5">
            <b-button
              type="is-primary"
              size="is-large"
              @click="scrollTo('finance')"
              >{{ $t("helpCenter.financing") }}</b-button
            >
            <b-button
              type="is-primary"
              size="is-large"
              @click="scrollTo('locations')"
              >{{ $t("helpCenter.locations") }}</b-button
            >
            <b-button
              type="is-primary"
              size="is-large"
              @click="scrollTo('faq')"
              >{{ $t("helpCenter.contactUs") }}</b-button
            >
          </div>
        </div>
      </div>
    </div>

    <div class="is-divider"></div>

    <div class="columns">
      <div class="column is-8 is-size-5 mt-5">
        <p class="title">{{ $t("helpCenter.whoAreWe") }}</p>

        {{ $t("helpCenter.whoAreWePartOne") }}<br />
        <br />
        <span v-html="`${$t('helpCenter.whoAreWePartTwo')}`"></span><br />

        <ul class="bullets ml-3 mt-2">
          <li>
            <b-icon icon="check-square"></b-icon>
            {{ $t("helpCenter.collab.bank") }}
          </li>
          <li>
            <b-icon icon="check-square"></b-icon>
            {{ $t("helpCenter.collab.finance") }}
          </li>
          <li>
            <b-icon icon="check-square"></b-icon>
            {{ $t("helpCenter.collab.publicAgencies") }}
          </li>
          <li>
            <b-icon icon="check-square"></b-icon>
            {{ $t("helpCenter.collab.dca") }}
          </li>
          <li>
            <b-icon icon="check-square"></b-icon>
            {{ $t("helpCenter.collab.lawyers") }}
          </li>
        </ul>
      </div>
      <div class="column is-4 is-vcentered">
        <figure class="image is-inline-block">
          <img
            class="is-rounded"
            style="width: 280px;"
            src="@/assets/img/team.webp"
          />
        </figure>
      </div>
    </div>
    <div class="is-divider"></div>
    <div class="columns">
      <div class="column is-4 is-vcentered">
        <figure class="image is-inline-block">
          <img
            class="is-rounded"
            style="width: 280px"
            src="@/assets/img/finance.webp"
          />
        </figure>
      </div>
      <div class="column is-8 is-size-5 mt-5">
        <p class="title" ref="finance">{{ $t("helpCenter.financing") }}</p>
        <span v-html="`${$t('helpCenter.financingDescription')}`"></span><br />
      </div>
    </div>
    <div class="is-divider"></div>
    <div class="columns">
      <div class="column is-full is-size-5 my-5">
        <p class="title" ref="locations">{{ $t("helpCenter.contactUs") }}</p>
        <b-tabs type="is-boxed">
          <b-tab-item
            :key="department.id"
            :label="department.name"
            v-for="department in departments"
          >
            <div class="columns">
              <div class="column is-6">
                <strong>{{ department.name }}</strong>

                <p class="mt-3">{{ department.address1 }}</p>
                <p v-if="department.address2 != ''">
                  {{ department.address2 }}
                </p>
                <p>{{ department.zip }} {{ department.city }}</p>
                <p>
                  <a :href="`mailto: ${department.email}`">{{
                    department.email
                  }}</a>
                </p>
              </div>
              <div class="column is-6">
                <div class="map-responsive">
                  <iframe
                    :id="`${department.id}`"
                    width="100%"
                    height="450"
                    frameborder="0"
                    style="border:0;"
                    :src="
                      `https://www.google.com/maps/embed/v1/place?key=AIzaSyDdVhh6dCJw1aSbIRUbKFiGaUwEgkeCEo0&q=${department.address1}, ${department.zip} ${department.city}`
                    "
                  >
                  </iframe>
                </div>
              </div>
            </div>
          </b-tab-item>
        </b-tabs>
      </div>
    </div>

    <div class="is-divider"></div>

    <div class="columns mt-5 is-size-5 is-multiline">
      <div class="column is-full has-text-centered">
        <p class="title" ref="faq">
          {{ $t("helpCenter.faq.title") }}
        </p>
        <div class="buttons is-centered mt-3">
          <b-button
            v-for="category in faqMenu"
            :key="category"
            :class="{ 'is-primary': activeFaq == category }"
            @click="activeFaq = category"
            >{{ category }}</b-button
          >
        </div>

        <b-collapse
          :open="false"
          class="card"
          animation="slide"
          :aria-id="`${item.category}-${item.title}`"
          v-for="item in faqItems"
          :key="item.title"
          @open="showContactHint = true"
        >
          <template #trigger="props" v-if="activeFaq == item.category">
            <div
              class="card-header"
              role="button"
              :aria-controls="`${item.category}-${item.title}`"
              :aria-expanded="props.open"
            >
              <p class="card-header-title">
                {{ item.title }}
              </p>
              <a class="card-header-icon">
                <b-icon :icon="props.open ? 'caret-up' : 'caret-down'">
                </b-icon>
              </a>
            </div>
          </template>

          <div class="card-content" v-if="activeFaq == item.category">
            <div
              class="content has-text-left"
              v-html="$nl2br(item.value)"
            ></div>
          </div>
        </b-collapse>
        <div>
          <p class="mt-5">
            {{ $t("helpCenter.faq.contactHint") }}
          </p>

          <b-button
            type="is-primary"
            class="mt-5"
            @click="showContactSection = !showContactSection"
            >{{ $t("helpCenter.faq.contactUs") }}</b-button
          >
        </div>
      </div>
    </div>

    <div
      ref="faq"
      class="columns is-dark notification is-light mt-5"
      v-if="showContactSection"
    >
      <div class="column  has-text-left">
        <p class="title">{{ $t("helpCenter.contactUs") }}</p>

        <span v-html="`${$t('helpCenter.contactUsDescription')}`"></span>

        <br /><br />{{ $t("helpCenter.phone") }}:
        <a href="tel:75587200">755 87 200</a> <br />{{
          $t("helpCenter.email")
        }}:
        <a href="mailto:sporsmal@stadssalg.no">sporsmal@stadssalg.no</a>
      </div>
      <div class="column is-4 is-vcentered">
        <figure class="image is-inline-block">
          <img
            class="is-rounded"
            style="width: 280px"
            src="@/assets/img/callus.webp"
          />
        </figure>
      </div>
    </div>
  </div>
</template>

<script>
import VueFaqAccordion from "vue-faq-accordion";

export default {
  components: {
    VueFaqAccordion
  },
  mounted() {
    this.$axios
      .get(`${window.apiURL}/locations`)
      .then(response => {
        this.isLoading = false;
        this.departments = response.data;
      })
      .catch(error => {
        this.isLoading = false;
        this.errorMessage = error.response.data.message
          ? error.response.data.message
          : error.message;
      });
  },
  data() {
    return {
      activeFaq: this.$t("helpCenter.faq.faq"),
      isLoading: true,
      showContactHint: false,
      showContactSection: false,
      departments: null,
      faqMenu: [this.$t("helpCenter.faq.faq")],
      faqItems: [
        {
          title: this.$t("helpCenter.faq.howToBid"),
          value: this.$t("helpCenter.faq.howToBidAnswer"),
          category: this.$t("helpCenter.faq.faq")
        },

        {
          title: this.$t("helpCenter.faq.whatTime"),
          value: this.$t("helpCenter.faq.whatTimeAnswer"),
          category: this.$t("helpCenter.faq.faq")
        },
        {
          title: this.$t("helpCenter.faq.whenPickup"),
          value: this.$t("helpCenter.faq.whenPickupAnswer"),
          category: this.$t("helpCenter.faq.faq")
        },
        {
          title: this.$t("helpCenter.faq.whenReadyForSale"),
          value: this.$t("helpCenter.faq.whenReadyForSaleAnswer"),
          category: this.$t("helpCenter.faq.faq")
        },
        {
          title: this.$t("helpCenter.faq.canIVisit"),
          value: this.$t("helpCenter.faq.canIVisitAnswer"),
          category: this.$t("helpCenter.faq.faq")
        },
        {
          title: this.$t("helpCenter.faq.doIGetNotification"),
          value: this.$t("helpCenter.faq.doIGetNotificationAnswer"),
          category: this.$t("helpCenter.faq.faq")
        }
      ]
    };
  },
  methods: {
    scrollTo(ref) {
      this.$refs[ref].scrollIntoView({ behavior: "smooth", block: "center" });
    },
    toggleFAQ(faqId) {
      this.isOpen[faqId] = !this.isOpen[faqId];
    }
  }
};
</script>

<style lang="scss" scoped>
ul.bullets {
  li {
    align-items: center !important;
    display: flex !important;

    span {
      margin-right: 0.5rem;
    }
  }
}
</style>
